import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { RootState } from '@/store/store';
import { hydrateFromLocalStorage, refreshStateForNewAd } from '@/editor/store/actions/hydration.action';

export interface IPropertyInfoState {
  propertyExtId: string | null;
  propertyCarouselExtId: string | null;
  propertyEstateType: string;
  propertyInternalId: string;
  companyId: string;
  propertyIsSold?: boolean;
  shareLink?: string;
  bidtheatreShareLink?: string;
  propertyAddress: string;
  orderTitle: string;
}

const initialState: IPropertyInfoState = {
  propertyExtId: '',
  propertyCarouselExtId: '',
  propertyEstateType: '',
  propertyInternalId: '',
  companyId: '',
  propertyIsSold: false,
  shareLink: '',
  bidtheatreShareLink: '',
  propertyAddress: '',
  orderTitle: '',
};

const propertyInfoSlice = createSlice({
  name: 'property-info-state',
  initialState,
  extraReducers: (builder) => {
    builder
      .addCase(refreshStateForNewAd, (state) => {
        return { ...initialState, companyId: state.companyId };
      })
      .addCase(hydrateFromLocalStorage, (state, action) => {
        const { propertyInfo } = action.payload;

        Object.assign(state, propertyInfo);
      });
  },
  reducers: {
    setPropertyExtId(state: IPropertyInfoState, action: PayloadAction<string>) {
      state.propertyExtId = action.payload;
    },
    setCarouselPropertyExtId(state: IPropertyInfoState, action: PayloadAction<string>) {
      state.propertyCarouselExtId = action.payload;
    },
    setPropertEstateType(state: IPropertyInfoState, action: PayloadAction<string>) {
      state.propertyEstateType = action.payload;
    },
    setPropertyInternalId(state: IPropertyInfoState, action: PayloadAction<string>) {
      state.propertyInternalId = action.payload;
    },
    setCompanyId(state: IPropertyInfoState, action: PayloadAction<string>) {
      state.companyId = action.payload;
    },
    setPropertyIsSold(state: IPropertyInfoState, action: PayloadAction<boolean>) {
      state.propertyIsSold = action.payload;
    },
    setShareLink(state: IPropertyInfoState, action: PayloadAction<string>) {
      state.shareLink = action.payload;
    },
    setBidtheatreShareLink(state: IPropertyInfoState, action: PayloadAction<string>) {
      state.bidtheatreShareLink = action.payload;
    },
    setPropertyAddress(state: IPropertyInfoState, action: PayloadAction<string>) {
      state.propertyAddress = action.payload;
    },
    setOrderTitle(state: IPropertyInfoState, action: PayloadAction<string>) {
      state.orderTitle = action.payload;
    },
  },
});

export const {
  setPropertyExtId,
  setPropertEstateType,
  setPropertyInternalId,
  setCompanyId,
  setPropertyIsSold,
  setCarouselPropertyExtId,
  setShareLink,
  setOrderTitle,
  setBidtheatreShareLink,
  setPropertyAddress,
} = propertyInfoSlice.actions;

export default propertyInfoSlice.reducer;

export const selectPropertyAddress = (state: RootState) => state.propertyInfoSliceReducer.propertyAddress;
export const selectExternalPropertyId = (state: RootState) => state.propertyInfoSliceReducer.propertyExtId;
export const selectInternalPropertyId = (state: RootState) => state.propertyInfoSliceReducer.propertyInternalId;
export const selectOrderTitle = (state: RootState) => state.propertyInfoSliceReducer.orderTitle;
